import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter } from 'lucide-react';
import type { SpriteCatalogIndex, SpriteCatalogEntry, SpriteLibrary } from '../sprites/spriteTypes';
import { fuzzySearchSprites } from '../sprites/fuzzySearch';
import { useCanvasActions } from '../hooks/useCanvasActions';
import { getSvgContent, preloadAllContent, loadLibraryContent, isLibraryLoaded } from '../sprites/spriteContentLoader';

function getLibraryOptions(t: (key: any) => string): Array<{ key: SpriteLibrary | 'all'; label: string }> {
  return [
    { key: 'all', label: t('canvasSprite.all') },
    { key: 'lucide', label: 'Lucide' },
    { key: 'heroicons', label: 'Heroicons' },
    { key: 'material', label: 'Material' },
    { key: 'fontawesome', label: 'Font Awesome' },
    { key: 'ionicons', label: 'Ionicons' },
    { key: 'fluentui', label: 'Fluent UI' },
    { key: 'bootstrap', label: 'Bootstrap' },
    { key: 'phosphor', label: 'Phosphor' },
    { key: 'tabler', label: 'Tabler' },
    { key: 'simple-icons', label: 'Brands' },
    { key: 'custom', label: t('canvasSprite.diagram') },
    { key: 'aws', label: 'AWS' },
    { key: 'azure', label: 'Azure' },
    { key: 'gcp', label: 'GCP' },
    { key: 'kubernetes', label: 'Kubernetes' },
  ];
}

const COLS = 4;
const ROW_HEIGHT = 48; // px per grid row
const OVERSCAN = 8;    // extra rows rendered above/below viewport

/** Resolve index entry + cached svgContent into a full SpriteCatalogEntry. */
function toFullEntry(entry: SpriteCatalogIndex): SpriteCatalogEntry | null {
  const svg = getSvgContent(entry.spriteId);
  if (!svg) return null;
  return { ...entry, svgContent: svg };
}

export function CanvasSpritePanel() {
  const { t } = useTranslation();
  const { handleAddSprite } = useCanvasActions();
  const [index, setIndex] = useState<SpriteCatalogIndex[] | null>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [libraryFilter, setLibraryFilter] = useState<SpriteLibrary | 'all'>('all');
  const scrollRef = useRef<HTMLDivElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(400);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  // Counter that increments as libraries load, triggering re-render for previews
  const [contentVersion, setContentVersion] = useState(0);

  // Lazy load index (lightweight, no svgContent)
  useEffect(() => {
    let cancelled = false;
    import('../sprites/spriteIndex.generated').then(mod => {
      if (!cancelled) setIndex(mod.SPRITE_INDEX);
    });
    return () => { cancelled = true; };
  }, []);

  // Preload all content in background once index is loaded
  useEffect(() => {
    if (!index) return;
    // If a specific library is filtered, prioritize that one
    const priorityLib = libraryFilter !== 'all' ? libraryFilter : null;
    if (priorityLib && !isLibraryLoaded(priorityLib)) {
      loadLibraryContent(priorityLib).then(() => setContentVersion(v => v + 1));
    }
    // Then preload everything
    preloadAllContent(() => {
      setContentVersion(v => v + 1);
    });
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  // When library filter changes, ensure that library's content is loaded
  useEffect(() => {
    if (libraryFilter !== 'all' && !isLibraryLoaded(libraryFilter)) {
      loadLibraryContent(libraryFilter).then(() => setContentVersion(v => v + 1));
    }
  }, [libraryFilter]);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Search results — no limit, virtual scroll handles rendering
  const results = useMemo(() => {
    if (!index) return [];
    return fuzzySearchSprites(debouncedQuery, index, libraryFilter, 0);
  }, [debouncedQuery, index, libraryFilter]);

  // Reset scroll on filter/query change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      setScrollTop(0);
    }
  }, [debouncedQuery, libraryFilter]);

  // Measure viewport height
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setViewportHeight(entry.contentRect.height);
    });
    ro.observe(el);
    setViewportHeight(el.clientHeight);
    return () => ro.disconnect();
  }, [index]);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) setScrollTop(scrollRef.current.scrollTop);
  }, []);

  // Virtual row calculation
  const totalRows = Math.ceil(results.length / COLS);
  const totalHeight = totalRows * ROW_HEIGHT;
  const startRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endRow = Math.min(totalRows, Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN);

  const visibleEntries = useMemo(() => {
    void contentVersion; // trigger re-compute when new library content loads
    const startIdx = startRow * COLS;
    const endIdx = endRow * COLS;
    return results.slice(startIdx, endIdx);
  }, [results, startRow, endRow, contentVersion]);

  const handleClick = useCallback((entry: SpriteCatalogIndex) => {
    const full = toFullEntry(entry);
    if (full) handleAddSprite(full);
  }, [handleAddSprite]);

  const handleDragStart = useCallback((e: React.DragEvent, entry: SpriteCatalogIndex) => {
    const full = toFullEntry(entry);
    if (!full) { e.preventDefault(); return; }
    e.dataTransfer.setData('application/x-canvas-sprite', JSON.stringify(full));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  // Loading state
  if (!index) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex items-center justify-center flex-1 text-xs text-gray-400">
          {t('canvasSprite.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Search + filter */}
      <div className="flex items-center gap-1 px-2 pt-2 pb-1 shrink-0">
        <input
          type="text"
          placeholder={t('canvasSprite.search')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          className="flex-1 min-w-0 px-2 py-1 text-xs text-gray-700 border border-gray-200 rounded focus:outline-none focus:border-blue-300"
        />
        <div style={{ position: 'relative' }}>
          <button
            ref={filterBtnRef}
            onClick={() => setFilterMenuOpen(v => !v)}
            title={t('canvasSprite.filterLibrary')}
            className={`flex items-center justify-center w-6 h-6 rounded transition-colors ${
              libraryFilter !== 'all'
                ? 'bg-blue-100 text-blue-600'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Filter size={12} />
          </button>
          {filterMenuOpen && (
            <LibraryFilterMenu
              current={libraryFilter}
              onSelect={(key) => { setLibraryFilter(key); setFilterMenuOpen(false); }}
              onClose={() => setFilterMenuOpen(false)}
            />
          )}
        </div>
      </div>

      {/* Virtual scroll grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden px-2" onScroll={handleScroll}>
        {results.length === 0 ? (
          <div className="text-xs text-gray-300 text-center py-4">
            {t('canvasSprite.noResults')}
          </div>
        ) : (
          <div style={{ height: totalHeight, position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                top: startRow * ROW_HEIGHT,
                left: 0,
                right: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 4,
              }}
            >
              {visibleEntries.map(entry => {
                const svg = getSvgContent(entry.spriteId);
                return (
                  <button
                    key={entry.spriteId}
                    title={entry.label}
                    onClick={() => handleClick(entry)}
                    draggable={!!svg}
                    onDragStart={(e) => handleDragStart(e, entry)}
                    style={{ height: ROW_HEIGHT }}
                    className="flex items-center justify-center rounded hover:bg-gray-50 transition-colors cursor-grab active:cursor-grabbing"
                  >
                    {svg ? (
                      <svg
                        viewBox={entry.viewBox}
                        width={24}
                        height={24}
                        dangerouslySetInnerHTML={{ __html: svg }}
                        style={entry.fillMode === 'native' ? {} : {
                          color: '#374151',
                          stroke: entry.fillMode === 'stroke' ? '#374151' : 'none',
                          fill: entry.fillMode === 'fill' ? '#374151' : 'none',
                          strokeWidth: entry.fillMode === 'stroke' ? 2 : undefined,
                          strokeLinecap: 'round',
                          strokeLinejoin: 'round',
                        }}
                      />
                    ) : (
                      <div className="w-6 h-6 rounded bg-gray-100" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LibraryFilterMenu({
  current,
  onSelect,
  onClose,
}: {
  current: SpriteLibrary | 'all';
  onSelect: (key: SpriteLibrary | 'all') => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const LIBRARY_OPTIONS = getLibraryOptions(t);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 4,
        zIndex: 50,
        minWidth: 120,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        padding: '4px 0',
      }}
    >
      {LIBRARY_OPTIONS.map(opt => (
        <button
          key={opt.key}
          onClick={() => onSelect(opt.key)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            width: '100%',
            padding: '5px 10px',
            fontSize: 11,
            color: current === opt.key ? '#2563eb' : '#374151',
            fontWeight: current === opt.key ? 500 : 400,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'left',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
        >
          {current === opt.key && <span style={{ fontSize: 9 }}>●</span>}
          {opt.label}
        </button>
      ))}
    </div>
  );
}
